import {
  CheckBoxWithLabelWidget,
  getMaxCharValidation,
  TabStyles
} from '@tableau/widgets';
import React from 'react';
import _ = require('underscore');

import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { divWithStyle } from '~src/style';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { Messages } from '../../codegen/Localize';
import { canSubmitDataQualityIndicator } from '../quality/canSubmitDataQualityIndicator';
import { isFormDataValidForSubmission } from '../quality/DataQualityDialog';
import { TextFormatterOrAreaWidget } from './components/TextFormatterOrAreaWidget';
import { markupEditorStyle, textAreaWidgetClass } from './styles/DataQualityStyles';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

const CERTIFICATION_DIALOG_TEST_ID: string = 'certification-dialog';
const CERTIFICATION_DIALOG_TOGGLE_TEST_ID: string = CERTIFICATION_DIALOG_TEST_ID + '-toggle';

const textAreaStyle: React.CSSProperties = {
  resize: 'none',
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
  height: 190,
  fontSize: TabStyles.Typography.Sizing.Default,
  marginBottom: TabStyles.Spacing.MediumSpace,
  padding: TabStyles.Spacing.DefaultSpace
};

const HelpTextDiv = divWithStyle(CERTIFICATION_DIALOG_TEST_ID, '-help-text', {
  paddingTop: TabStyles.Spacing.SmallSpace,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

const checkboxStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.SmallSpace,
};

export type CertificationDialogState = {
  readonly certificationNote: string;
  readonly isCertified: boolean;
}

export type CertificationDialogBodyProps = {
  readonly useMarkup: boolean;
}
export type CertificationActionDialogOptions = ReactActionDialogOptions<CertificationDialogBodyProps, CertificationDialogState, unknown>;
export type CertificationDialogBodyComponentProps = ReactActionDialogBodyProps<CertificationDialogBodyProps, CertificationDialogState>;

export function CertificationDialogBody(props: CertificationDialogBodyComponentProps): React.ReactElement {
  const handleCertificationChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ certificationNote: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState]
  );
  const { message, valid } = getMaxCharValidation(props.dialogState.certificationNote, MESSAGE_MAXIMUM_LENGTH);

  const useMarkup = FeatureFlags.markupEditingEnabled;

  return (
    <>
      <CheckBoxWithLabelWidget
        label={Messages.certificationCheckboxLabel()}
        testId={CERTIFICATION_DIALOG_TOGGLE_TEST_ID}
        checked={props.dialogState.isCertified}
        handleChange={(checked: boolean) => props.updateDialogState({ isCertified: checked })}
        removeLeftMargin={true}
        containerStyle={checkboxStyle} />
      <HelpTextDiv>{Messages.certificationHelpNote()}</HelpTextDiv>
      <TextFormatterOrAreaWidget
        label={Messages.certificationNoteLabel()}
        onChange={handleCertificationChange}
        id={CERTIFICATION_DIALOG_TEST_ID}
        testId={CERTIFICATION_DIALOG_TEST_ID}
        valid={valid}
        message={message}
        className={useMarkup ? null : textAreaWidgetClass}
        style={useMarkup ? markupEditorStyle : textAreaStyle}
        markup={props.dialogState.certificationNote}
      />
    </>
  );
}

/**
 * Data Quality Service will throw if we send an enabled certification with a blank note
 */
function checkIsValid(current: CertificationDialogState): boolean {
  const command = canSubmitDataQualityIndicator({ message: current.certificationNote, active: current.isCertified });
  return command === 'SUBMIT' || command === 'DELETE';
}

export function getCertificationActionDialogOptions(args: {
  initialBodyProps: CertificationDialogBodyProps;
  initialFormData: CertificationDialogState;
  doAction: (formData: CertificationDialogState) => Promise<unknown>;
}): CertificationActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction } = args;

  return {
    title: Messages.certificationLabel(),
    bodyComponent: CertificationDialogBody,
    initialBodyProps: initialBodyProps,
    initialDialogState: initialFormData,
    actionName: Messages.save(),
    action: (_, dialogState) => doAction(dialogState),
    actionDisabled: (_, dialogState) => !isFormDataValidForSubmission({
      initialFormData: { isCertified: initialFormData.isCertified, message: initialFormData.certificationNote },
      currentFormData: { isCertified: dialogState.isCertified, message: dialogState.certificationNote },
      additionalValidation: x => checkIsValid({ isCertified: x.isCertified, certificationNote: x.message })
    }),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  };
}
